import {Actions, Button, ButtonActionProps, Flag, Parameters} from 'platform/components';
import {Box, Heading, HStack, Show, StorageImage, VStack} from 'platform/foundation';
import styled from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {always, isNil, isNotEmpty} from 'ramda';

import {SimilarVehicleResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';

import {composePath, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {thumbnailUrl} from '../../../constants/thumbnailUrl';
import {useVehicleConditionEvaluation} from '../../../hooks/useVehicleConditionEvaluation';
import {useVehiclePhotosPermissions} from '../../../hooks/useVehiclePhotosPermissions';
import {VehicleRecommendationType} from '../../../types/VehicleRecommendationType';
import {getVehicleCoverPhoto} from '../../../utils/getVehicleCoverPhoto';
import {getVehicleTitle} from '../../../utils/getVehicleTitle';
import {queryParams} from '../../../utils/queryParams';
import {VehicleConditionEvaluation} from '../../EntityHeader/components/VehicleConditionEvaluation';
import {useVehicleInformationParameters} from '../hooks/useVehicleInformationParameters';

export type VehicleSelectionType =
  | 'createSaleProcess'
  | 'createServiceProcess'
  | 'createRentalProcess'
  | 'choose';

interface VehiclePreviewProps extends RequiredTestIdProps {
  vehicle: SimilarVehicleResponseBody;
  shouldRecommendVehiclesFor?: VehicleRecommendationType;
  onSelect: (selectionType: VehicleSelectionType) => void;
  hideChooseButton?: boolean;
  additionalActions?: (vehicle: SimilarVehicleResponseBody) => ButtonActionProps[];
}

export function VehiclePreview(props: VehiclePreviewProps) {
  const navigate = useNavigate();
  const {canReadVehicleAlbumPhotos} = useVehiclePhotosPermissions({
    vehicleId: props.vehicle.id,
  });

  const coverPhoto = getVehicleCoverPhoto(props.vehicle.photos ?? []);
  const vehicleTitle = props.vehicle.title ?? getVehicleTitle(props.vehicle);

  const vehicleParameters = useVehicleInformationParameters(props.vehicle);
  const [vehicleConditionEvaluation] = useVehicleConditionEvaluation(props.vehicle.id);

  const chooseVehicleButtonProps = match([
    props.shouldRecommendVehiclesFor,
    isNil(props.vehicle.serviceVehicleId),
    isNil(props.vehicle.saleVehicleId),
    isNil(props.vehicle.rentalVehicleId),
  ])
    .with(
      ['service', true, Pattern.boolean, Pattern.boolean],
      always({
        title: i18n.t('entity.vehicle.actions.createServiceVehicle'),
        onClick: () => props.onSelect('createServiceProcess'),
      })
    )
    .with(
      ['sale', Pattern.boolean, true, Pattern.boolean],
      always({
        title: i18n.t('entity.vehicle.actions.createSaleVehicle'),
        onClick: () => props.onSelect('createSaleProcess'),
      })
    )
    .with(
      ['rental', Pattern.boolean, Pattern.boolean, true],
      always({
        title: i18n.t('entity.vehicle.actions.createRentalProcess'),
        onClick: () => props.onSelect('createRentalProcess'),
      })
    )
    .otherwise(always(null));

  return (
    <HStack spacing={4}>
      <ImageFilterDiv $isArchived={props.vehicle.isArchived}>
        <Box
          borderRadius="small"
          overflow="hidden"
          width={25}
          height={18}
          data-testid={suffixTestId(`image`, props)}
        >
          <StorageImage
            shouldUseOriginal={!coverPhoto || !canReadVehicleAlbumPhotos}
            url={canReadVehicleAlbumPhotos ? (coverPhoto?.url ?? thumbnailUrl) : thumbnailUrl}
            width={25}
            height={18}
            alt={vehicleTitle}
            fit="cover"
          />
        </Box>
      </ImageFilterDiv>

      <VStack justify="center">
        <Show when={props.vehicle.isArchived}>
          <Flag colorScheme="neutral" label={i18n.t('entity.vehicle.labels.archived')} />
        </Show>

        <Heading size={4} data-testid={suffixTestId(`title`, props)}>
          {vehicleTitle}
        </Heading>

        <Parameters
          parameters={vehicleParameters}
          size="small"
          color="secondary"
          appendix={
            <VehicleConditionEvaluation
              size="small"
              color="secondary"
              vehicleCondition={vehicleConditionEvaluation}
              data-testid={suffixTestId(`overallConditions`, props)}
            />
          }
        />
      </VStack>

      <HStack justify="flex-end" align="center" spacing={2} grow={1}>
        <Button
          variant="outlined"
          title={i18n.t('entity.vehicle.labels.detail')}
          leftIcon="action/open_in_new"
          onClick={() => {
            const returnUrl = composePath(vehiclesRoutes.detail, {
              params: {id: props.vehicle.id},
              isSearchParamsPreserved: false,
            });
            const searchParams = new URLSearchParams({
              [queryParams.RETURN_URL]: returnUrl,
            });

            navigate({
              pathname: composePath(vehiclesRoutes.editV2, {
                params: {id: props.vehicle.id},
                isSearchParamsPreserved: false,
              }),
              search: `?${searchParams.toString()}`,
            });
          }}
        />

        <Show when={isNotEmpty(props.additionalActions ?? [])}>
          <Actions
            actions={props.additionalActions ? props.additionalActions(props.vehicle) : []}
            data-testid={suffixTestId('additionalActions', props)}
          />
        </Show>

        <Show when={!props.hideChooseButton && chooseVehicleButtonProps !== null}>
          <Button {...chooseVehicleButtonProps!} />
        </Show>
      </HStack>
    </HStack>
  );
}

const ImageFilterDiv = styled.div<{$isArchived: boolean | null}>`
  ${({$isArchived}) => $isArchived && `filter: grayscale(100%)`}
`;
